import { usePostHog } from 'posthog-js/react'
import type { PostHog } from 'posthog-js'
import { useEffect } from 'react'
import { useLocation, useSearchParams } from 'react-router'
import { useAuth, useUser } from '@clerk/clerk-react'
import * as Sentry from '@sentry/react'

import { ANALYTICS_EVENTS } from 'app/utils/constants'
import useBraze from 'app/hooks/useBraze'
import type { PersonalLoansV2ApiOfferGetOfferData200 } from 'app/api/api.schemas'

/**
 * Returns true when PostHog has an identified (vs anonymous) user for the current session.
 *
 * We gate identify()/reset() on this so we only identify once and only reset a user that
 * was actually identified. PostHog stores identification state in the `$user_state` person
 * property ('identified' vs 'anonymous'); this reads it via the supported public `get_property`
 * API instead of the private `_isIdentified()`, which is unsupported and can break on upgrade.
 */
function isPosthogIdentified(posthog: PostHog): boolean {
    return posthog.get_property('$user_state') === 'identified'
}

export default function PostHogPageView({ offerData }: { offerData?: PersonalLoansV2ApiOfferGetOfferData200 }): null {
    const location = useLocation()
    const searchParams = useSearchParams()
    const posthog = usePostHog()
    const { executeBrazeOperation } = useBraze()
    const { isSignedIn } = useAuth()
    const { user } = useUser()

    // Track pageviews on page load
    useEffect(() => {
        if (location.pathname && posthog) {
            const { search, origin } = window.location
            let url = origin + location.pathname

            if (search) {
                url += search
            }
            posthog.capture(ANALYTICS_EVENTS.PAGE_VIEWED, {
                $current_url: url,
                apr: offerData?.apr,
                min_loan_amount: offerData?.min_loan_amount,
                max_loan_amount: offerData?.max_loan_amount
            })
        }
    }, [location.pathname, searchParams, posthog])

    useEffect(() => {
        const data = offerData?.data as any
        const externalId = data?.external_id as string
        // If the external ID is provided, identify the user in braze and open a session
        // If no external ID is provided, assume the offer data is not available and do nothing
        if (externalId) {
            // Identify the user in braze
            executeBrazeOperation((braze) => {
                braze.changeUser(externalId)
            })

            // open a session in braze after calling changeUser
            executeBrazeOperation((braze) => {
                braze.openSession()
            })
        }
    }, [offerData])

    useEffect(() => {
        if (!posthog) return

        const figUserId = user?.externalId
        const isIdentified = isPosthogIdentified(posthog)

        // 👉 Check the sign in status and user info,
        //    and identify the user if they aren't already
        if (isSignedIn && user && !isIdentified && !!figUserId) {
            // Identify the user in posthog
            posthog.identify(figUserId, {
                email: user.primaryEmailAddress?.emailAddress,
                username: user.username
            })

            Sentry.setUser({
                id: figUserId,
                email: user.primaryEmailAddress?.emailAddress
            })
        }

        // Reset the user identification in posthog/braze if they sign out
        if (!isSignedIn && isIdentified) {
            posthog.reset()
            Sentry.setUser(null)
        }
    }, [posthog, user, executeBrazeOperation, isSignedIn])

    return null
}
